/**
 * Page: FreeUsers
 * Admin-only tool to manage a whitelist of emails that receive free premium
 * access. Supports adding/removing emails, revoking access, and shows status.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { LoadingSpinner, ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { ErrorMessage } from '../../../components/ui/feedback/error-message.jsx';
import { ArrowLeft, Gift, Plus, Trash2, Mail, UserCheck, X, CheckCircle, UserMinus } from 'lucide-react';
import { Input } from '../../../components/ui/inputs/input';
import { Button } from '../../../components/ui/buttons/button';
import { Label } from '../../../components/ui/inputs/label';
import Footer from '../../../components/shared/Footer';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import api from '../../../api';
import { del } from '../../../api/core';

export default function FreeUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  
  // Free user emails state
  const [freeUserEmails, setFreeUserEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [addEmailError, setAddEmailError] = useState('');
  
  // Revoke access state
  const [revokingEmail, setRevokingEmail] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(null);
  
  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error && !showError) {
      const timer = setTimeout(() => setShowError(true), 200);
      return () => clearTimeout(timer);
    }
  }, [error, showError]);

  // Admin check and redirect if not authorized
  useEffect(() => {
    if (user && !isLoading) {
      if (!isAdmin) {
        navigate('/app');
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [user, isAdmin, navigate, isLoading]);

  // Fetch free user emails on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchFreeUsers();
    }
  }, [isAdmin]);

  const fetchFreeUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setShowError(false);
      
      const response = await api.get('/admin/free-users');
      setFreeUserEmails(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch free user emails');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEmail = async () => {
    // Basic email validation
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) {
      setAddEmailError('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    if (freeUserEmails.some(entry => entry.email.toLowerCase() === newEmail.toLowerCase())) {
      setAddEmailError('This email is already in the free users list');
      return;
    }

    try {
      setIsSubmitting(true);
      setAddEmailError('');
      
      const response = await api.post('/admin/free-users', {
        email: newEmail.toLowerCase().trim()
      });
      
      // Add the new email to our local state
      setFreeUserEmails([...freeUserEmails, response.data]);
      setNewEmail('');
      
      addToast(`${newEmail} has been added to the free users list`, 'success');
    } catch (err) {
      setAddEmailError(err.message || 'Failed to add email to free users list');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveEmail = async (emailId) => {
    const emailEntry = freeUserEmails.find(e => e.id === emailId);
    if (!emailEntry) return;

    try {
      await del(`/admin/free-users/${emailId}`);
      
      // Remove from local state
      setFreeUserEmails(freeUserEmails.filter(e => e.id !== emailId));
      
      addToast(`${emailEntry.email} has been removed from the free users list`, 'success');
    } catch (err) {
      addToast(err.message || 'Failed to remove email from free users list', 'error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRevokeAccess = async (emailEntry) => {
    if (!emailEntry.isUsed) {
      addToast('This email is not currently in use', 'error');
      return;
    }

    try {
      setRevokingEmail(emailEntry.email);
      
      const response = await api.admin.revokeUserAccess(emailEntry.email);
      
      // Update local state to reflect the changes
      setFreeUserEmails(prevEmails => 
        prevEmails.map(entry => 
          entry.id === emailEntry.id 
            ? { 
                ...entry, 
                isUsed: false, 
                userId: null, 
                usedAt: null,
                revokedAt: new Date().toISOString()
              }
            : entry
        )
      );
      
      addToast(
        `Successfully revoked access for ${emailEntry.email}. User has been downgraded to basic plan and their subscription cancelled.`,
        'success'
      );
      
      setShowRevokeConfirm(null);
    } catch (err) {
      addToast(err.message || 'Failed to revoke user access', 'error');
    } finally {
      setRevokingEmail(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading free users..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-4 sm:p-6 w-full max-w-[1600px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      {error && showError && (
        <div className="mb-4">
          <ErrorMessage 
            message={error} 
            onDismiss={() => setError(null)} 
          />
        </div>
      )}

      {/* Page header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-start sm:items-center mb-4 flex-col sm:flex-row gap-2 sm:gap-0">
          <div className="flex items-center">
            <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">Free Users Management</h1>
          </div>
        </div>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage email addresses that are eligible for free premium subscriptions. These users will receive 100% discount coupons during checkout.
        </p>
      </div>

      {/* Add new email section */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">
          Add Free User Email
        </h3>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label htmlFor="email" className="sr-only">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter email address (e.g., user@example.com)"
              className="w-full"
              disabled={isSubmitting}
            />
            {addEmailError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{addEmailError}</p>
            )}
          </div>
          <Button
            type="button"
            onClick={handleAddEmail}
            disabled={isSubmitting || !newEmail.trim()}
            className="sm:w-auto w-full"
          >
            {isSubmitting ? (
              <>
                <ButtonSpinner />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Email
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Free users list */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
            Free User Emails ({freeUserEmails.length})
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Email addresses that receive free premium subscriptions
          </p>
        </div>

        <div className="p-4 sm:p-6">
          {freeUserEmails.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Gift className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Free Users
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                No email addresses have been added to the free users list yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {freeUserEmails.map((emailEntry) => (
                <div
                  key={emailEntry.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-transparent border border-gray-200 dark:border-gray-600 rounded-lg gap-3 sm:gap-0"
                >
                  <div className="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
                    <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                      {emailEntry.isUsed ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Mail className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                        {emailEntry.email}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Added: {new Date(emailEntry.createdAt).toLocaleDateString()}</span>
                        {emailEntry.isUsed ? (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-1 space-y-1 sm:space-y-0">
                            <div className="flex items-center space-x-1">
                              <UserCheck className="h-3 w-3" />
                              <span className="break-all">Used by: {emailEntry.userId}</span>
                            </div>
                            <span className="hidden sm:inline">•</span>
                            <span>Used: {new Date(emailEntry.usedAt).toLocaleDateString()}</span>
                          </div>
                        ) : (
                          <span className="text-yellow-600 dark:text-yellow-400">Unused</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                    {emailEntry.isUsed && (
                      <button
                        type="button"
                        onClick={() => setShowRevokeConfirm(emailEntry)}
                        disabled={revokingEmail === emailEntry.email}
                        className="p-2 text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 transition-colors disabled:opacity-50"
                        title="Revoke free access (downgrades user to basic plan)"
                      >
                        {revokingEmail === emailEntry.email ? (
                          <ButtonSpinner />
                        ) : (
                          <UserMinus className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(emailEntry.id)}
                      className="p-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                      title="Remove email"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
      
      {/* Revoke Access Confirmation Dialog */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900 mx-4">
            <button
              onClick={() => setShowRevokeConfirm(null)}
              className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/50">
                  <UserMinus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Revoke Free Access
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This action will downgrade the user to basic plan
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Are you sure you want to revoke free access for:
                </p>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                    {showRevokeConfirm.email}
                  </p>
                </div>
              </div>
              
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                  This action will:
                </p>
                <ul className="space-y-1 text-xs text-orange-700 dark:text-orange-300">
                  <li>• Cancel their Stripe subscription immediately</li>
                  <li>• Downgrade the user to basic plan</li>
                  <li>• Reset this email entry to "unused" status</li>
                  <li>• Allow this email to be used for free access again</li>
                  <li>• Keep their account and data intact</li>
                </ul>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRevokeConfirm(null)}
                disabled={revokingEmail === showRevokeConfirm.email}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevokeAccess(showRevokeConfirm)}
                disabled={revokingEmail === showRevokeConfirm.email}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-orange-500 disabled:bg-orange-500 disabled:opacity-50"
              >
                {revokingEmail === showRevokeConfirm.email ? (
                  <>
                    <ButtonSpinner />
                    Revoking...
                  </>
                ) : (
                  <>
                    <UserMinus className="h-4 w-4" />
                    Revoke Access
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 